import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import BackButton from '../../../components/feature/BackButton';
import { supabase } from '../../../lib/supabase';

interface Actualite {
  id: string;
  titre: string;
  description: string;
  contenu: string;
  type: 'evenement' | 'don_sang' | 'campagne' | 'information' | 'autre';
  image_url: string | null;
  date_debut: string | null;
  date_fin: string | null;
  lieu: string | null;
  epingle: boolean;
  date_creation: string;
  vues: number;
}

export default function ActualiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [actualite, setActualite] = useState<Actualite | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedActualites, setRelatedActualites] = useState<Actualite[]>([]);

  useEffect(() => {
    if (id) {
      fetchActualite();
      incrementViews();
    }
  }, [id]);

  const fetchActualite = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('actualites')
        .select('*')
        .eq('id', id)
        .eq('actif', true)
        .single();

      if (error) throw error;
      
      setActualite(data);

      // Charger les actualités similaires
      if (data) {
        const { data: related } = await supabase
          .from('actualites')
          .select('*')
          .eq('actif', true)
          .eq('type', data.type)
          .neq('id', id)
          .order('date_creation', { ascending: false })
          .limit(3);

        setRelatedActualites(related || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'actualité:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async () => {
    try {
      await supabase.rpc('increment_actualite_views', { actualite_id: id });
    } catch (error) {
      console.error('Erreur lors de l\'incrémentation des vues:', error);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      evenement: 'Événement',
      don_sang: 'Don de sang',
      campagne: 'Campagne',
      information: 'Information',
      autre: 'Autre'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      evenement: 'ri-calendar-event-line',
      don_sang: 'ri-heart-pulse-line',
      campagne: 'ri-megaphone-line',
      information: 'ri-information-line',
      autre: 'ri-file-list-line'
    };
    return icons[type] || 'ri-file-list-line';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      evenement: 'bg-purple-500',
      don_sang: 'bg-red-500',
      campagne: 'bg-blue-500',
      information: 'bg-green-500',
      autre: 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <i className="ri-loader-4-line text-5xl text-clinic-green animate-spin mb-4"></i>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!actualite) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <i className="ri-error-warning-line text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Actualité introuvable</h2>
            <p className="text-gray-600 mb-8">Cette actualité n'existe pas ou a été supprimée</p>
            <Link
              to="/actualites"
              className="inline-flex items-center gap-2 px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Retour aux actualités
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-clinic-green to-clinic-blue text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <BackButton />
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className={`${getTypeColor(actualite.type)} text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2 whitespace-nowrap`}>
              <i className={getTypeIcon(actualite.type)}></i>
              {getTypeLabel(actualite.type)}
            </div>
            {actualite.epingle && (
              <div className="bg-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2 whitespace-nowrap">
                <i className="ri-pushpin-fill"></i>
                Épinglé
              </div>
            )}
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            {actualite.titre}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-white/90">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{formatDate(actualite.date_creation)}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-eye-line"></i>
              <span>{actualite.vues} vues</span>
            </div>
          </div>
        </div>
      </section>

      {/* Image principale */}
      {actualite.image_url && (
        <section className="relative">
          <div className="w-full overflow-hidden">
            <img
              src={actualite.image_url}
              alt={actualite.titre}
              className="w-full h-auto max-h-[600px] object-contain"
            />
          </div>
        </section>
      )}

      {/* Contenu principal */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Informations événement */}
          {actualite.type === 'evenement' && (actualite.date_debut || actualite.lieu) && (
            <div className="mb-12 p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl border-2 border-purple-200">
              <h3 className="text-2xl font-bold text-purple-900 mb-6 flex items-center gap-3">
                <i className="ri-calendar-event-line text-3xl"></i>
                Informations pratiques
              </h3>
              
              <div className="space-y-4">
                {actualite.date_debut && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-500 text-white rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-line text-2xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-purple-900 mb-1">Date</p>
                      <p className="text-purple-700">
                        {formatDate(actualite.date_debut)}
                        {actualite.date_fin && actualite.date_fin !== actualite.date_debut && 
                          ` - ${formatDate(actualite.date_fin)}`
                        }
                      </p>
                    </div>
                  </div>
                )}
                
                {actualite.lieu && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-500 text-white rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-purple-900 mb-1">Lieu</p>
                      <p className="text-purple-700">{actualite.lieu}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-8 p-6 bg-clinic-blue/5 rounded-2xl border-2 border-clinic-blue/10">
            <p className="text-lg text-gray-700 leading-relaxed font-medium">
              {actualite.description}
            </p>
          </div>

          {/* Contenu */}
          <div className="prose prose-lg max-w-none">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {actualite.contenu}
            </div>
          </div>

          {/* Bouton de contact */}
          <div className="mt-12 p-8 bg-gradient-to-br from-clinic-blue/10 to-clinic-green/10 rounded-3xl border-2 border-clinic-blue/20 text-center">
            <h3 className="text-2xl font-bold text-clinic-blue-dark mb-4">
              Besoin de plus d'informations ?
            </h3>
            <p className="text-gray-600 mb-6">
              Notre équipe est à votre disposition pour répondre à toutes vos questions
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-mail-line text-2xl"></i>
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Actualités similaires */}
      {relatedActualites.length > 0 && (
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-clinic-blue-dark mb-12 text-center">
              Actualités similaires
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedActualites.map((related) => (
                <Link
                  key={related.id}
                  to={`/actualites/${related.id}`}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-gray-100 hover:border-clinic-green/30"
                >
                  {/* Image */}
                  <div className="relative h-48 bg-gradient-to-br from-clinic-blue/10 to-clinic-green/10 overflow-hidden">
                    {related.image_url ? (
                      <img
                        src={related.image_url}
                        alt={related.titre}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className={`${getTypeIcon(related.type)} text-6xl text-clinic-blue/30`}></i>
                      </div>
                    )}
                    
                    <div className={`absolute top-4 left-4 ${getTypeColor(related.type)} text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2 whitespace-nowrap`}>
                      <i className={getTypeIcon(related.type)}></i>
                      {getTypeLabel(related.type)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-clinic-blue-dark mb-3 line-clamp-2 group-hover:text-clinic-green transition-colors">
                      {related.titre}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {related.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-eye-line"></i>
                        <span>{related.vues} vues</span>
                      </div>
                      
                      <span className="text-clinic-green font-semibold hover:text-clinic-green/80 transition-colors flex items-center gap-2 whitespace-nowrap">
                        Lire plus
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/actualites"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-clinic-blue border-2 border-clinic-blue rounded-full hover:bg-clinic-blue hover:text-white transition-all font-semibold whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Voir toutes les actualités
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
